/*
 * Client-side image compression before upload (ADR-0030; target amended by
 * ADR-0031). Stored images are capped at 8 MB as a storage policy — the family
 * gallery shouldn't accumulate multi-tens-of-MB originals when the largest
 * served variant is the 1200² viewer. Instead of rejecting big photos, the
 * picker re-encodes them to fit: longest edge capped at 3000px and JPEG quality
 * stepped down until the file is under 8 MiB. (The original ~2 MB target
 * mirrored webhuset's PHP upload limit; those hosting limits were raised to 32M
 * on 2026-07-03, so the threshold is now policy, not plumbing.)
 *
 * Fail-open by design: if the browser can't decode the file (HEIC in Chrome),
 * canvas isn't available, or encoding doesn't help, the ORIGINAL file is
 * returned unchanged and the server's validation stays the authority.
 */

// The storage-policy cap (ADR-0031): files at or below 8 MiB pass through
// untouched; bigger picks are re-encoded until they fit under it.
export const COMPRESS_THRESHOLD_BYTES = 8 * 1024 * 1024;

// Longest-edge cap for the re-encode (ADR-0030's quality-per-byte trade-off,
// 2.5× the served 1200² viewer variant; unchanged by the ADR-0031 target raise).
const MAX_EDGE = 3000;

// Quality ladder — try the best first, step down until under the threshold.
const QUALITIES = [0.82, 0.72, 0.6, 0.5];

// Decode via an <img> element (not createImageBitmap) so EXIF orientation is
// applied uniformly across Chromium/WebKit/Firefox — drawImage() of a decoded
// <img> uses the oriented pixels in all modern browsers.
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Kunne ikke dekode bildet i nettleseren.'));
    };
    image.src = url;
  });
}

function encodeJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

/**
 * Re-encode an oversized image to fit the effective upload limit; returns the
 * original file untouched when it already fits or when compression can't run.
 */
export async function compressImageForUpload(file: File): Promise<File> {
  if (file.size <= COMPRESS_THRESHOLD_BYTES) return file;

  try {
    const image = await loadImage(file);
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    if (width === 0 || height === 0) return file;

    const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const context = canvas.getContext('2d');
    if (context === null) return file;

    // White backdrop so transparent PNG regions don't turn black in the JPEG.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    let encoded: Blob | null = null;
    for (const quality of QUALITIES) {
      encoded = await encodeJpeg(canvas, quality);
      if (encoded !== null && encoded.size <= COMPRESS_THRESHOLD_BYTES) break;
    }
    // Keep the original if encoding failed or somehow produced a bigger file.
    if (encoded === null || encoded.size >= file.size) return file;

    const stem = file.name.replace(/\.[^.]+$/, '');
    return new File([encoded], `${stem}.jpg`, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}
